import { useSession } from '@/providers/session/SessionProvider';
import React, { createContext, PropsWithChildren, useContext } from 'react';
import { AuthenticateUserWithPasswordResponse, REDEEM_USER_PASSWORD_RESET_TOKEN, RedeemUserPasswordResetTokenResponse, SEND_USER_PASSWORD_RESET_LINK, SIGN_IN, UPDATE_USER_PASSWORD } from './gql';
import createClient from 'openapi-fetch';
import { paths } from '../../api/openapi/schema';
import { auth } from '@/api/openapi/middleware';
import { CreateApolloClient } from '@/api/client/apollo';
import { CustomSession } from '../session/type';
import { useApolloClient } from '@apollo/client';

interface AuthContextType {
    login: (email: string, password: string) => Promise<CustomSession>;
    logout: () => Promise<void>;
    register: (email: string) => Promise<void>;
    createAccount: (token: string, password: string, name: string) => Promise<CustomSession>;
    requestResetPassword: (email: string) => Promise<void>;
    resetPassword: (email: string, token: string, password: string) => Promise<void>;
    updatePassword: (token: string, currentPassword: string, newPassword: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const client = useApolloClient();
    const { clear } = useSession();

    const login = async (email: string, password: string): Promise<CustomSession> => {
        const response = await client.mutate<AuthenticateUserWithPasswordResponse>({
            mutation: SIGN_IN,
            variables: {
                email,
                password,
            },
        });

        const result = response.data?.authenticateUserWithPassword;

        if (result?.__typename === 'UserAuthenticationWithPasswordSuccess') {
            return { token: result.sessionToken, user: { id: result.item.id, name: result.item.name, email: result.item.email, role: result.item.role } };
        } else {
            throw new Error(result?.message || 'Authentication failed.');
        }
    };

    const logout = async () => {
        await clear();
        return;
    };

    const register = async (email: string) => {
        const client = createClient<paths>({ baseUrl: `${import.meta.env.VITE_API_SERVICE_URL}/v1` });

        const { error } = await client.POST('/emails/verification', {
            body: {
                email,
            },
        });

        if (error) {
            throw new Error(error.error);
        }

        return;
    };

    const createAccount = async (token: string, password: string, name: string): Promise<CustomSession> => {
        const client = createClient<paths>({ baseUrl: `${import.meta.env.VITE_API_SERVICE_URL}/v1` });
        client.use(auth(token));

        const { error, data } = await client.POST('/users', {
            body: {
                name,
                password,
            },
        });

        if (error) {
            throw new Error(error.error);
        }

        return { token: data.sessionToken };
    };

    const requestResetPassword = async (email: string) => {
        await client.mutate<AuthenticateUserWithPasswordResponse>({
            mutation: SEND_USER_PASSWORD_RESET_LINK,
            variables: {
                email,
            },
        });
    };

    const resetPassword = async (email: string, token: string, password: string) => {
        const response = await client.mutate<RedeemUserPasswordResetTokenResponse>({
            mutation: REDEEM_USER_PASSWORD_RESET_TOKEN,
            variables: {
                email,
                token,
                password,
            },
        });

        if (response.data?.redeemUserPasswordResetToken?.code) {
            throw new Error(response.data?.redeemUserPasswordResetToken.message);
        }
    };

    const updatePassword = async (token: string, currentPassword: string, newPassword: string) => {
        // intentionally to create a new apollo client so that we don't use the cached local storage token
        const client = CreateApolloClient(token);
        return await client
            .mutate<{ updateUserPassword: boolean }>({
                mutation: UPDATE_USER_PASSWORD,
                variables: {
                    token,
                    currentPassword,
                    newPassword,
                },
            })
            .then((response) => {
                return response.data?.updateUserPassword || false;
            });
    };

    return (
        <AuthContext.Provider
            value={{
                login,
                logout,
                register,
                createAccount,
                requestResetPassword,
                resetPassword,
                updatePassword,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
};
